import React, { useState } from 'react';
import { Card, Form, Row, Col, Badge } from 'react-bootstrap';
import './PlayerPicker.css';

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

/**
 * PlayerPicker - Responsive component for selecting players
 * 
 * Features:
 * - Desktop: 4-5 column grid with hover effects
 * - Mobile: 2 column grid with larger touch targets
 * - Support for singles (1 player) and doubles (2 players)
 * - Visual feedback on selection
 * - Search/filter capability
 * 
 * @param {Array} players - List of available players
 * @param {Array} selectedPlayers - Currently selected player IDs
 * @param {Function} onSelect - Callback when player is selected/deselected
 * @param {Number} maxSelection - Maximum number of players to select (1 for singles, 2 for doubles)
 * @param {String} teamName - Name of the team (for display)
 */
const PlayerPicker = ({
    players = [],
    selectedPlayers = [],
    onSelect,
    maxSelection = 1,
    teamName = ''
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const handlePlayerClick = (playerId) => {
        if (selectedPlayers.includes(playerId)) {
            // Deselect player
            onSelect(selectedPlayers.filter(id => id !== playerId));
        } else {
            // Select player
            if (selectedPlayers.length < maxSelection) {
                onSelect([...selectedPlayers, playerId]);
            } else if (maxSelection === 1) {
                // For singles, replace the selection
                onSelect([playerId]);
            } else {
                // For doubles, replace the oldest selection
                onSelect([...selectedPlayers.slice(1), playerId]);
            }
        }
    };

    const filteredPlayers = players.filter(player =>
        player.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="player-picker">
            <div className="player-picker-header">
                <h5 className="mb-3">
                    {teamName && <Badge bg="info" className="me-2">{teamName}</Badge>}
                    Chọn {maxSelection === 1 ? 'Cầu thủ' : `${maxSelection} Cầu thủ`}
                </h5>

                {players.length > 6 && (
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="🔍 Tìm kiếm cầu thủ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </Form.Group>
                )}

                <div className="selection-info mb-3">
                    <small className="text-muted">
                        Đã chọn: {selectedPlayers.length}/{maxSelection}
                    </small>
                </div>
            </div>

            <div className="player-list-compact">
                {filteredPlayers.map(player => {
                    const isSelected = selectedPlayers.includes(player.id);
                    const selectionIndex = selectedPlayers.indexOf(player.id);

                    return (
                        <div 
                            key={player.id} 
                            className={`player-list-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handlePlayerClick(player.id)}
                        >
                            <span className="player-name">{player.name}</span>
                            {isSelected && (
                                <Badge bg="success" className="ms-2">
                                    {maxSelection > 1 ? `#${selectionIndex + 1}` : '✓'}
                                </Badge>
                            )}
                        </div>
                    );
                })}
            </div>

            {filteredPlayers.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <p>Không tìm thấy cầu thủ nào</p>
                </div>
            )}
        </div>
    );
};

export default PlayerPicker;
